import React from "react";
import { useTranslation } from "react-i18next";
import { safeHtml } from "@/lib/safeHtml";
import WantedProfileButton from "@/components/WantedProfileButton";
import { Check } from "lucide-react";

/**
 * Bottom-of-page FDP catalog: either interactive picker (partner / sales
 * manual / admin / simple-manual mode) or info-only grid (everyone else).
 * Empty categories show a "Wanted profile" CTA so users can request that
 * FDP starts stocking that range.
 */
export default function CatalogSection({
  simType,
  isInterior,
  isSales,
  salesMode,
  user,
  catalog,
  style,
  selectedCats,
  onToggleCat,
  usingCatalogPicker,
  maxCats,
}) {
  const { t } = useTranslation();

  // Per-style admin archival + global "auto-hide empty" setting.
  // - When a style is picked, drop any category whose `hidden_styles` list
  //   contains that style (admin archived it for this style).
  // - When `settings.auto_hide_empty_categories` is true AND a style is
  //   picked, also drop categories with 0 photos for that style.
  const autoHideEmpty = !!catalog?.settings?.auto_hide_empty_categories;
  const visibleCategories = React.useMemo(() => {
    const all = catalog?.categories || [];
    if (!style) return all;
    return all.filter((c) => {
      if (Array.isArray(c.hidden_styles) && c.hidden_styles.includes(style)) {
        return false;
      }
      if (autoHideEmpty) {
        const count = (c.photos_by_style && c.photos_by_style[style]) || 0;
        if (count === 0) return false;
      }
      return true;
    });
  }, [catalog, style, autoHideEmpty]);

  if (!catalog) return null;

  // Total products visible for the active style — used in the right-hand
  // KPI on the header strip. Falls back to overall catalog total when no
  // style is picked yet.
  const totalForStyle = style
    ? visibleCategories.reduce(
        (sum, c) => sum + ((c.photos_by_style || {})[style] || 0),
        0,
      )
    : catalog.total_products;

  return (
    <section className="mt-16" data-testid="catalog-section">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-2">
            {usingCatalogPicker
              ? t("simulate.catalog_section_picker_title")
              : t("simulate.catalog_section_info_title")}
          </div>
          <h2 className="font-outfit font-light text-white text-3xl md:text-4xl tracking-tighter">
            {usingCatalogPicker
              ? t("simulate.catalog_picker_heading", {
                  selected: selectedCats.length,
                  max: maxCats,
                })
              : t("simulate.catalog_info_heading", {
                  type: isInterior
                    ? t("simulate.type_interior")
                    : t("simulate.type_exterior"),
                })}
          </h2>
          {usingCatalogPicker && (
            <p
              className="text-zinc-400 text-sm mt-2 max-w-xl"
              dangerouslySetInnerHTML={safeHtml(
                t("simulate.catalog_picker_desc", { role: user?.role }),
              )}
            />
          )}
          {isSales && salesMode === "AUTO" && (
            <p
              className="text-zinc-400 text-sm mt-2 max-w-xl"
              dangerouslySetInnerHTML={safeHtml(
                t("simulate.catalog_auto_desc", {
                  style: style || t("simulate.catalog_style_chosen"),
                }),
              )}
            />
          )}
        </div>
        <div className="text-right">
          <div className="font-outfit text-3xl font-black text-orange-500">
            {totalForStyle}+
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
            {style
              ? t("simulate.catalog_count_style", {
                  style: style.replace("_", " "),
                  n: visibleCategories.length,
                })
              : t("simulate.catalog_count_total", { n: visibleCategories.length })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {visibleCategories.map((c) => {
          const isSelected = selectedCats.includes(c.slug);
          const totalCount = c.uploaded_photos_count || 0;
          const styleCount =
            style && c.photos_by_style ? c.photos_by_style[style] || 0 : null;
          const photoCount = styleCount !== null ? styleCount : totalCount;
          const isEmpty = photoCount === 0;

          if (!usingCatalogPicker) {
            return (
              <div
                key={c.slug}
                className={`relative border rounded-lg overflow-hidden transition-colors ${
                  isEmpty
                    ? "border-white/5 bg-zinc-900/20"
                    : "border-white/10 bg-zinc-900/30 hover:border-orange-500/30 hover:bg-zinc-900/60"
                }`}
                data-testid={`catalog-cat-${c.slug}`}
              >
                {c.image && (
                  <div className="aspect-[4/3] bg-zinc-950 overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.name}
                      className={`w-full h-full object-cover ${
                        isEmpty ? "grayscale opacity-60" : ""
                      }`}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                )}
                <div className={`p-3 ${isEmpty ? "pb-11" : ""}`}>
                  <div className="font-outfit font-bold text-white text-sm leading-tight">
                    {c.name}
                  </div>
                  <div className="text-xs mt-2 uppercase tracking-[0.15em]">
                    {isEmpty ? (
                      <span className="px-1.5 py-0.5 rounded bg-zinc-700/40 text-zinc-400 text-[9px] font-bold">
                        {t("simulate.catalog_unavailable")}
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[9px] font-bold">
                        {photoCount}{" "}
                        {photoCount === 1
                          ? t("simulate.catalog_profile_singular")
                          : t("simulate.catalog_profile_plural")}
                      </span>
                    )}
                  </div>
                </div>
                {isEmpty && (
                  <WantedProfileButton
                    simType={simType}
                    categorySlug={c.slug}
                    categoryName={c.name}
                  />
                )}
              </div>
            );
          }

          return (
            <div key={c.slug} className="relative">
              <button
                type="button"
                onClick={() => onToggleCat(c.slug)}
                className={`w-full relative text-left border-2 rounded-lg overflow-hidden transition-all duration-300 ${
                  isSelected
                    ? "border-orange-500 ring-2 ring-orange-500/30 bg-orange-500/5"
                    : isEmpty
                    ? "border-white/5 bg-zinc-900/20 opacity-80 cursor-not-allowed"
                    : "border-white/10 hover:border-white/30 bg-zinc-900/40"
                }`}
                data-testid={`pick-cat-${c.slug}`}
              >
                {c.image && (
                  <div className="aspect-[4/3] bg-zinc-950 overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.name}
                      className={`w-full h-full object-cover transition-transform duration-500 ${
                        isEmpty ? "grayscale" : "hover:scale-110"
                      }`}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                )}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white shadow-lg">
                    <Check size={16} strokeWidth={2.5} />
                  </div>
                )}
                {isEmpty && !isSelected && (
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-zinc-800/90 backdrop-blur text-zinc-300 text-[9px] uppercase font-bold tracking-wider">
                    {t("simulate.catalog_empty_badge")}
                  </div>
                )}
                <div className={`p-3 ${isEmpty ? "pb-11" : ""}`}>
                  <div className="font-outfit font-bold text-white text-sm leading-tight">
                    {c.name}
                  </div>
                  <div className="text-xs mt-2 uppercase tracking-[0.15em]">
                    {isEmpty ? (
                      <span className="px-1.5 py-0.5 rounded bg-zinc-700/40 text-zinc-400 text-[9px] font-bold">
                        {t("simulate.catalog_unavailable")}
                      </span>
                    ) : (
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          isSelected
                            ? "bg-orange-500/20 text-orange-300"
                            : "bg-emerald-500/20 text-emerald-400"
                        }`}
                      >
                        {photoCount}{" "}
                        {photoCount === 1
                          ? t("simulate.catalog_profile_singular")
                          : t("simulate.catalog_profile_plural")}
                      </span>
                    )}
                  </div>
                </div>
              </button>
              {isEmpty && (
                <WantedProfileButton
                  simType={simType}
                  categorySlug={c.slug}
                  categoryName={c.name}
                />
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-600 mt-6 text-center">
        {t("simulate.catalog_footer")}
      </p>
    </section>
  );
}
